/**
 * Client-side video file utilities: metadata probing (duration, dimensions)
 * and thumbnail extraction. Both use a local object URL — no network involved.
 */

export interface VideoFileMetadata {
  duration: number;
  width: number;
  height: number;
  isVertical: boolean;
}

/** Probe a video File for duration and dimensions. */
export function probeVideoFile(file: File): Promise<VideoFileMetadata> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    const url = URL.createObjectURL(file);

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Timed out reading video metadata'));
    }, 15000);

    const cleanup = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      video.load();
    };

    video.addEventListener('loadedmetadata', () => {
      const duration = video.duration;
      const width = video.videoWidth;
      const height = video.videoHeight;
      cleanup();
      if (!Number.isFinite(duration) || duration <= 0) {
        reject(new Error('Could not determine video duration'));
        return;
      }
      resolve({
        duration,
        width,
        height,
        isVertical: height > width,
      });
    }, { once: true });

    video.addEventListener('error', () => {
      cleanup();
      reject(new Error('Failed to load video file for metadata'));
    }, { once: true });

    video.src = url;
  });
}

/**
 * Extract a thumbnail image from a video file.
 * Seeks to `timeInSeconds`, plays until a real frame is decoded, then captures
 * it to a canvas.
 */
export async function extractVideoThumbnail(
  videoFile: File,
  timeInSeconds: number = 1,
): Promise<File> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Failed to get canvas 2D context'));
      return;
    }

    video.muted = true;
    video.playsInline = true;

    const videoUrl = URL.createObjectURL(videoFile);
    video.src = videoUrl;

    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('Timed out extracting thumbnail'));
    }, 20000);

    let settled = false;
    const cleanup = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(videoUrl);
      video.pause();
      video.removeAttribute('src');
      video.load();
    };

    const captureCurrentFrame = () => {
      if (settled) return;
      settled = true;

      try {
        const w = video.videoWidth;
        const h = video.videoHeight;

        if (!w || !h) {
          cleanup();
          reject(new Error('Video has no displayable frame (zero dimensions)'));
          return;
        }

        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(video, 0, 0, w, h);

        canvas.toBlob(
          (blob) => {
            cleanup();
            if (!blob) {
              reject(new Error('canvas.toBlob() returned null'));
              return;
            }
            const safeName = videoFile.name.replace(/\.[^.]+$/, '.jpg');
            resolve(new File([blob], `thumbnail_${safeName}`, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          0.85,
        );
      } catch (err) {
        cleanup();
        reject(err);
      }
    };

    const playAndCapture = () => {
      if (typeof (video as { requestVideoFrameCallback?: unknown }).requestVideoFrameCallback === 'function') {
        video.requestVideoFrameCallback(() => {
          video.pause();
          captureCurrentFrame();
        });
        video.play().catch(() => captureCurrentFrame());
      } else {
        const onTimeUpdate = () => {
          video.removeEventListener('timeupdate', onTimeUpdate);
          video.pause();
          captureCurrentFrame();
        };
        video.addEventListener('timeupdate', onTimeUpdate);
        video.play().catch(() => {
          video.removeEventListener('timeupdate', onTimeUpdate);
          captureCurrentFrame();
        });
      }
    };

    video.addEventListener('seeked', playAndCapture, { once: true });

    video.addEventListener('loadedmetadata', () => {
      const duration = video.duration ?? 0;
      const seekTo = duration > 0
        ? Math.min(timeInSeconds, Math.max(0, duration - 0.1))
        : 0;
      video.currentTime = seekTo;
    }, { once: true });

    video.addEventListener('error', () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('Video failed to load for thumbnail extraction'));
    }, { once: true });

    video.load();
  });
}
